import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlusIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { logger } from '../lib/logging';
import { useAuth } from '../contexts/AuthContext';
import { TextStyleToolbar } from '../components/TextStyleToolbar';

interface BannerAnnouncement {
  id: string;
  message: string;
  priority: 'info' | 'warning' | 'critical';
  start_at: string;
  end_at: string;
  cta_url: string | null;
  cta_text: string | null;
  is_published: boolean;
  audience: 'all' | 'authenticated';
  created_at: string;
  updated_at: string;
}

interface BannerFormData {
  message: string;
  priority: 'info' | 'warning' | 'critical';
  start_at: string;
  end_at: string;
  cta_url: string;
  cta_text: string;
  is_published: boolean;
  audience: 'all' | 'authenticated';
}

interface AdminAlertsPageProps {
  embedded?: boolean;
  correlationId?: string;
}

export const AdminAlertsPage: React.FC<AdminAlertsPageProps> = ({
  embedded = false,
  correlationId,
}) => {
  const { user } = useAuth();
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [banners, setBanners] = useState<BannerAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerAnnouncement | null>(null);
  const [previewBanner, setPreviewBanner] = useState<BannerFormData | null>(null);

  const correlationIdValue = useMemo(
    () => correlationId ?? crypto.randomUUID(),
    [correlationId]
  );
  const ctx = useMemo(
    () => logger.withCorrelation(correlationIdValue),
    [correlationIdValue]
  );

  const [formData, setFormData] = useState<BannerFormData>({
    message: '',
    priority: 'info',
    start_at: new Date().toISOString().slice(0, 16),
    end_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
    cta_url: '',
    cta_text: '',
    is_published: false,
    audience: 'all',
  });

  const fetchBanners = useCallback(async () => {
    if (!user) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      logger.info('admin_alerts.fetch_start', undefined, ctx);

      const idToken = await user.getIdToken();

      const response = await fetch('/api/banners', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch banners');
      }

      const data = await response.json();
      setBanners(data.banners || []);

      logger.info('admin_alerts.fetch_success', undefined, {
        ...ctx,
        count: data.banners?.length || 0,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      logger.error('admin_alerts.fetch_error', message, ctx);
    } finally {
      setLoading(false);
    }
  }, [ctx, user]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError('Not authenticated');
      return;
    }

    setError(null);

    try {
      const payload = {
        ...formData,
        cta_url: formData.cta_url.trim() || null,
        cta_text: formData.cta_text.trim() || null,
      };

      const url = editingBanner
        ? `/api/banners/${editingBanner.id}`
        : '/api/banners';
      const method = editingBanner ? 'PATCH' : 'POST';

      logger.info(
        editingBanner ? 'admin_alerts.update_start' : 'admin_alerts.create_start',
        undefined,
        { ...ctx, bannerId: editingBanner?.id }
      );

      const idToken = await user.getIdToken();

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save banner');
      }

      logger.info(
        editingBanner ? 'admin_alerts.update_success' : 'admin_alerts.create_success',
        undefined,
        ctx
      );

      // Reset form and close
      setFormData({
        message: '',
        priority: 'info',
        start_at: new Date().toISOString().slice(0, 16),
        end_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 16),
        cta_url: '',
        cta_text: '',
        is_published: false,
        audience: 'all',
      });
      setShowForm(false);
      setEditingBanner(null);
      setPreviewBanner(null);

      // Refresh list
      await fetchBanners();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      logger.error('admin_alerts.save_error', message, ctx);
    }
  };

  const handleEdit = (banner: BannerAnnouncement) => {
    setEditingBanner(banner);
    setFormData({
      message: banner.message,
      priority: banner.priority,
      start_at: new Date(banner.start_at).toISOString().slice(0, 16),
      end_at: new Date(banner.end_at).toISOString().slice(0, 16),
      cta_url: banner.cta_url || '',
      cta_text: banner.cta_text || '',
      is_published: banner.is_published,
      audience: banner.audience,
    });
    setShowForm(true);
    setPreviewBanner(null);
  };

  const handleDelete = async (bannerId: string) => {
    if (!user) {
      setError('Not authenticated');
      return;
    }

    if (!confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      logger.info('admin_alerts.delete_start', undefined, { ...ctx, bannerId });

      const idToken = await user.getIdToken();

      // Optimistically remove from UI
      setBanners((prev) => prev.filter((b) => b.id !== bannerId));

      const response = await fetch(`/api/banners/${bannerId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        // If delete fails, refresh to restore the banner
        await fetchBanners();
        throw new Error('Failed to delete banner');
      }

      logger.info('admin_alerts.delete_success', undefined, { ...ctx, bannerId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      logger.error('admin_alerts.delete_error', message, { ...ctx, bannerId });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBanner(null);
    setPreviewBanner(null);
    setFormData({
      message: '',
      priority: 'info',
      start_at: new Date().toISOString().slice(0, 16),
      end_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 16),
      cta_url: '',
      cta_text: '',
      is_published: false,
      audience: 'all',
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-900/30 border-red-500/50 text-red-200';
      case 'warning':
        return 'bg-yellow-900/30 border-yellow-500/50 text-yellow-200';
      case 'info':
        return 'bg-blue-900/30 border-blue-500/50 text-blue-200';
      default:
        return 'bg-slate-800/50 border-slate-700/60 text-slate-300';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'info':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const isActive = (banner: BannerAnnouncement) => {
    const now = new Date();
    const start = new Date(banner.start_at);
    const end = new Date(banner.end_at);
    return banner.is_published && now >= start && now <= end;
  };

  const content = (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold text-white">Announcement Banners</h2>
          <p className="text-sm text-slate-400">
            Manage site-wide alerts and announcements
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchBanners}
            disabled={loading}
            className="px-4 py-2 bg-slate-800/50 border border-slate-700/60 text-slate-300 rounded-lg hover:border-slate-500/60 disabled:opacity-50 flex items-center gap-2"
          >
            <ArrowPathIcon
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingBanner(null);
            }}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            New Banner
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-200">
          {error}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-slate-800/50 border border-slate-700/60 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white">
            {editingBanner ? 'Edit Banner' : 'Create New Banner'}
          </h3>

          <form onSubmit={handleCreateOrUpdate} className="space-y-4">
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Message *
              </label>
              <TextStyleToolbar
                textareaRef={messageTextareaRef}
                onStyleApply={(styledMessage) =>
                  setFormData({ ...formData, message: styledMessage })
                }
              />
              <textarea
                ref={messageTextareaRef}
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                required
                rows={3}
                placeholder="Enter banner message (supports basic HTML)"
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700/60 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Priority *
              </label>
              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priority: e.target.value as 'info' | 'warning' | 'critical',
                  })
                }
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700/60 rounded-lg text-white focus:outline-none focus:border-cyan-500"
              >
                <option value="info">Info (Blue)</option>
                <option value="warning">Warning (Yellow)</option>
                <option value="critical">Critical (Red)</option>
              </select>
            </div>

            {/* Dates */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-300">
                  Schedule *
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const oneWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                      setFormData({
                        ...formData,
                        start_at: now.toISOString().slice(0, 16),
                        end_at: oneWeek.toISOString().slice(0, 16),
                      });
                    }}
                    className="px-3 py-1 text-xs bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                  >
                    1 Week
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const oneMonth = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                      setFormData({
                        ...formData,
                        start_at: now.toISOString().slice(0, 16),
                        end_at: oneMonth.toISOString().slice(0, 16),
                      });
                    }}
                    className="px-3 py-1 text-xs bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                  >
                    1 Month
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const oneDay = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                      setFormData({
                        ...formData,
                        start_at: now.toISOString().slice(0, 16),
                        end_at: oneDay.toISOString().slice(0, 16),
                      });
                    }}
                    className="px-3 py-1 text-xs bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                  >
                    24 Hours
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Start Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_at}
                    onChange={(e) =>
                      setFormData({ ...formData, start_at: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700/60 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    {formData.start_at
                      ? new Date(formData.start_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : 'Select start date'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    End Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_at}
                    onChange={(e) =>
                      setFormData({ ...formData, end_at: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700/60 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    {formData.end_at
                      ? new Date(formData.end_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : 'Select end date'}
                  </p>
                </div>
              </div>

              {/* Duration Display */}
              {formData.start_at && formData.end_at && (
                <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-800/30 px-3 py-2 rounded-lg">
                  <span className="font-medium">Duration:</span>
                  <span>
                    {(() => {
                      const start = new Date(formData.start_at);
                      const end = new Date(formData.end_at);
                      const diffMs = end.getTime() - start.getTime();
                      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                      if (diffDays > 0) {
                        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
                      }
                      return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
                    })()}
                  </span>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  CTA URL
                </label>
                <input
                  type="url"
                  value={formData.cta_url}
                  onChange={(e) =>
                    setFormData({ ...formData, cta_url: e.target.value })
                  }
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700/60 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  CTA Text
                </label>
                <input
                  type="text"
                  value={formData.cta_text}
                  onChange={(e) =>
                    setFormData({ ...formData, cta_text: e.target.value })
                  }
                  placeholder="Learn More"
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700/60 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>

            {/* Published */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) =>
                  setFormData({ ...formData, is_published: e.target.checked })
                }
                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-cyan-600 focus:ring-cyan-500"
              />
              <label htmlFor="is_published" className="text-sm text-slate-300">
                Publish immediately (visible to users within schedule)
              </label>
            </div>

            {/* Audience Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Audience
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:text-white transition-colors">
                  <input
                    type="radio"
                    name="audience"
                    value="all"
                    checked={formData.audience === 'all'}
                    onChange={() => setFormData({ ...formData, audience: 'all' })}
                    className="w-4 h-4 text-cyan-600 border-slate-700 bg-slate-900 focus:ring-cyan-500"
                  />
                  <span>🌍 All users (including anonymous visitors)</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:text-white transition-colors">
                  <input
                    type="radio"
                    name="audience"
                    value="authenticated"
                    checked={formData.audience === 'authenticated'}
                    onChange={() => setFormData({ ...formData, audience: 'authenticated' })}
                    className="w-4 h-4 text-cyan-600 border-slate-700 bg-slate-900 focus:ring-cyan-500"
                  />
                  <span>🔒 Logged-in users only</span>
                </label>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Control who can see this banner
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg"
              >
                {editingBanner ? 'Update Banner' : 'Create Banner'}
              </button>
              <button
                type="button"
                onClick={() => setPreviewBanner(formData)}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2"
              >
                <EyeIcon className="w-4 h-4" />
                Preview
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 bg-slate-800/50 border border-slate-700/60 text-slate-300 rounded-lg hover:border-slate-500/60"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Preview */}
      {previewBanner && (
        <div className="bg-slate-800/50 border border-slate-700/60 rounded-xl p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Preview</h3>
            <button
              onClick={() => setPreviewBanner(null)}
              className="text-slate-400 hover:text-white"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>

          <div
            className={`border rounded-lg p-4 ${getPriorityColor(previewBanner.priority)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div
                  className="text-sm"
                  dangerouslySetInnerHTML={{ __html: previewBanner.message }}
                />
              </div>
              {previewBanner.cta_url && previewBanner.cta_text && (
                <a
                  href={previewBanner.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors"
                  onClick={(e) => e.preventDefault()}
                >
                  {previewBanner.cta_text}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Banner List */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-white">
          All Banners ({banners.length})
        </h3>

        {loading && banners.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            No banners created yet. Click "New Banner" to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map((banner) => (
              <div
                key={banner.id}
                className="bg-slate-800/30 border border-slate-700/60 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded border ${getPriorityBadge(banner.priority)}`}
                      >
                        {banner.priority.toUpperCase()}
                      </span>
                      {isActive(banner) ? (
                        <span className="px-2 py-1 text-xs font-medium rounded border bg-green-500/20 text-green-300 border-green-500/30">
                          ACTIVE
                        </span>
                      ) : banner.is_published ? (
                        <span className="px-2 py-1 text-xs font-medium rounded border bg-slate-500/20 text-slate-300 border-slate-500/30">
                          SCHEDULED
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded border bg-slate-700/20 text-slate-400 border-slate-700/30">
                          DRAFT
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${
                        banner.audience === 'authenticated'
                          ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                          : 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                      }`}>
                        {banner.audience === 'authenticated' ? '🔒 Logged-in only' : '🌍 All users'}
                      </span>
                    </div>

                    <div
                      className="text-slate-200"
                      dangerouslySetInnerHTML={{ __html: banner.message }}
                    />

                    <div className="text-xs text-slate-400 space-y-1">
                      <div>
                        Schedule: {new Date(banner.start_at).toLocaleString()} →{' '}
                        {new Date(banner.end_at).toLocaleString()}
                      </div>
                      {banner.cta_url && banner.cta_text && (
                        <div>
                          CTA: {banner.cta_text} → {banner.cta_url}
                        </div>
                      )}
                      <div>
                        Created: {new Date(banner.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(banner)}
                      className="p-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded-lg"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(banner.id)}
                      className="p-2 bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded-lg"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return embedded ? content : <div className="min-h-screen">{content}</div>;
};
